import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { useTranslation } from 'react-i18next';

/**
 * Component that renders children only if user has required permission
 */
/**
 * Renders children when the current user has the requested permission(s).
 * - Accepts either `permission` (single) or `permissions` (array).
 */
export const PermissionGuard = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children
}) => {
  const { checkPermission, checkAnyPermission, userRole } = useRole();

  // If no user role, don't render
  if (!userRole) {
    return fallback;
  }

  // Check single permission
  if (permission) {
    const hasAccess = checkPermission(permission);
    return hasAccess ? children : fallback;
  }

  // Check multiple permissions
  if (permissions && Array.isArray(permissions)) {
    const hasAccess = requireAll
      ? permissions.every(perm => checkPermission(perm))
      : checkAnyPermission(permissions);

    return hasAccess ? children : fallback;
  }

  return fallback;
};

/**
 * Component that renders children only if user has required role
 */
/**
 * Renders children when the user's role matches one of the allowed roles.
 * Supports single role string or array of roles.
 */
export const RoleGuard = ({
  roles,
  requireAll = false,
  fallback = null,
  children
}) => {
  const { userRole } = useRole();

  if (!userRole) {
    return fallback;
  }

  // Convert single role to array
  const roleArray = Array.isArray(roles) ? roles : [roles];

  const hasAccess = requireAll
    ? roleArray.every(role => userRole === role)
    : roleArray.includes(userRole);

  return hasAccess ? children : fallback;
};

/**
 * Component that renders children only for Super Admin
 */
/** Renders children for Super Admin users only */
export const SuperAdminGuard = ({ fallback = null, children }) => {
  return (
    <RoleGuard roles={USER_ROLES.SUPER_ADMIN} fallback={fallback}>
      {children}
    </RoleGuard>
  );
};

/**
 * Component that renders children only for Admin and Super Admin
 */
export const AdminGuard = ({ fallback = null, children }) => {
  return (
    <RoleGuard
      roles={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
      fallback={fallback}
    >
      {children}
    </RoleGuard>
  );
};

/**
 * Component that renders children for Super Admin, Admin, and Sales Staff
 */
export const StaffGuard = ({ fallback = null, children }) => {
  return (
    <RoleGuard
      roles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATIONS_MANAGER, USER_ROLES.SALES_STAFF, USER_ROLES.DELIVERY_SUPERVISOR, USER_ROLES.ACCOUNT_USER]}
      fallback={fallback}
    >
      {children}
    </RoleGuard>
  );
};

/**
 * Higher-order component for role-based access control
 */
/**
 * Higher-order component that enforces role-based access on a component.
 * - `requiredPermissions` is an array of permission keys; if provided, component is shown when
 *   the user has any of those permissions.
 */
export const withRoleAccess = (WrappedComponent, requiredPermissions = []) => {
  return function RoleAccessComponent(props) {
    const { t } = useTranslation();
    const { checkAnyPermission, userRole } = useRole();

    if (!userRole) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {t('access.authenticationRequired.title')}
            </h2>
            <p className="text-gray-600">{t('access.authenticationRequired.message')}</p>
          </div>
        </div>
      );
    }

    if (requiredPermissions.length > 0 && !checkAnyPermission(requiredPermissions)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {t('access.accessDenied.title')}
            </h2>
            <p className="text-gray-600">{t('access.accessDenied.message')}</p>
          </div>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Route guard component for protecting routes
 */
export const RouteGuard = ({
  path,
  fallback = null,
  children
}) => {
  const { t } = useTranslation();
  const { checkRouteAccess } = useRole();

  const hasAccess = checkRouteAccess(path);

  if (!hasAccess) {
    return fallback || (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {t('access.accessDenied.title')}
          </h2>
          <p className="text-gray-600">{t('access.accessDenied.message')}</p>
        </div>
      </div>
    );
  }

  return children;
};